import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  ScrollAreaCorner,
  ScrollAreaRoot,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "reka-ui";
import { defineComponent, ref } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Icon from "../../ui/icon/Icon";

const Root = DialogRoot;

const Trigger = defineComponent({
  name: "DialogTrigger",
  inheritAttrs: false,
  props: {
    asChild: p.boolean(),
    as: p.string<keyof HTMLElementTagNameMap>("button"),
  },
  setup(props, { slots }) {
    return () => <DialogTrigger {...props}>{slots.default?.()}</DialogTrigger>;
  },
});

const Content = defineComponent({
  name: "DialogContent",
  inheritAttrs: false,
  emits: {
    closeAutoFocus: (_e: Event) => true,
    dismiss: () => true,
    escapeKeyDown: (_e: Event) => true,
    focusOutside: (_e: Event) => true,
    interactOutside: (_e: Event) => true,
    openAutoFocus: (_e: Event) => true,
    pointerDownOutside: (_e: Event) => true,
  },
  props: {
    asChild: p.boolean(),
    as: p.string<keyof HTMLElementTagNameMap>(),
    disableOutsidePointerEvents: p.boolean(false),
    forceMount: p.boolean(false),
    class: p.string(),
    backdrop: p.boolean(true),
    showClose: p.boolean(true),
  },
  setup(props, { slots, emit }) {
    // Reka parks focus on the content box when a dialog opens, so a child
    // marked `autofocus` never receives it. Honour that mark here instead of
    // in every dialog: the first one wins, and dialogs without it keep the
    // default behaviour.
    const focusMarkedChild = (event: Event) => {
      const content = event.currentTarget;
      if (!(content instanceof HTMLElement)) return;
      const target = content.querySelector("[autofocus]");
      if (!(target instanceof HTMLElement)) return;
      event.preventDefault();
      target.focus();
    };

    return () => (
      <DialogPortal>
        <DialogOverlay
          class={cn(
            "fixed inset-0 z-70 py-20 open:animate-in open:fade-in-0 closed:animate-out closed:fade-out-0",
            props.backdrop && "bg-white/80 backdrop-blur-sm dark:bg-black/80"
          )}
        >
          <DialogContent
            class={cn(
              "fixed left-[50%] top-[50%] z-70 flex w-[calc(100vw-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-2 rounded-lg border bg-card shadow-lg duration-200 open:animate-in open:fade-in-0 open:zoom-in-95 closed:animate-out closed:fade-out-0 closed:zoom-out-95 md:w-full",
              props.class
            )}
            disableOutsidePointerEvents={props.disableOutsidePointerEvents}
            forceMount={props.forceMount}
            onCloseAutoFocus={props.asChild ? undefined : (e: Event) => emit("closeAutoFocus", e)}
            onEscapeKeyDown={props.asChild ? undefined : (e: Event) => emit("escapeKeyDown", e)}
            onFocusOutside={props.asChild ? undefined : (e: Event) => emit("focusOutside", e)}
            onInteractOutside={props.asChild ? undefined : (e: Event) => emit("interactOutside", e)}
            onOpenAutoFocus={
              props.asChild
                ? undefined
                : (e: Event) => {
                    focusMarkedChild(e);
                    emit("openAutoFocus", e);
                  }
            }
            onPointerDownOutside={
              props.asChild ? undefined : (e: Event) => emit("pointerDownOutside", e)
            }
          >
            {slots.default?.()}
            {props.showClose && (
              <DialogClose class="absolute right-3 top-3 rounded-md transition-colors hover:bg-muted focus:outline-dashed focus:outline-1 focus:outline-ring">
                <Icon icon="lucide:x" class="h-4 w-4" />
                <span class="sr-only">Close</span>
              </DialogClose>
            )}
          </DialogContent>
        </DialogOverlay>
      </DialogPortal>
    );
  },
});

const Header = defineComponent({
  name: "DialogHeader",
  props: {
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => (
      <div class={cn("mx-6 mb-3 mt-6 flex flex-col gap-y-2 text-center sm:text-left", props.class)}>
        {slots.default?.()}
      </div>
    );
  },
});

const Body = defineComponent({
  name: "DialogBody",
  props: {
    class: p.string(),
  },
  setup(props, { slots, expose }) {
    const viewportComp = ref<{ $el: HTMLElement } | null>(null);

    expose({
      scrollToBottom(options?: { behavior?: "auto" | "smooth" }) {
        const scrollableEl = viewportComp.value?.$el.parentElement;
        if (!scrollableEl) return;
        scrollableEl.scrollTo({
          top: viewportComp.value?.$el.scrollHeight,
          behavior: options?.behavior ?? "smooth",
        });
      },
    });
    return () => (
      <div class="mx-2">
        <ScrollAreaRoot class={cn("relative", props.class)} type="auto">
          <ScrollAreaViewport
            ref={viewportComp}
            class={cn(
              "max-h-[calc(100vh-14rem)] scroll-smooth rounded px-4 py-2 focus-visible:outline-dashed focus-visible:outline-1 focus-visible:outline-ring sm:max-h-[calc(100vh-200px)]",
              props.class
            )}
          >
            {slots.default?.()}
          </ScrollAreaViewport>
          <ScrollAreaScrollbar
            class={cn(
              "group flex touch-none select-none rounded-full bg-muted transition-colors",
              "h-full w-1.5 border-l border-l-transparent p-px"
            )}
          >
            <ScrollAreaThumb class="flex-1 rounded-full bg-muted-foreground/50 transition-colors group-hover:bg-muted-foreground" />
          </ScrollAreaScrollbar>
          <ScrollAreaCorner />
        </ScrollAreaRoot>
      </div>
    );
  },
});

const Footer = defineComponent({
  name: "DialogFooter",
  props: {
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => (
      <div
        class={cn(
          "m-6 mt-3 flex flex-col space-y-2 sm:flex-row sm:justify-end sm:space-x-2 sm:space-y-0",
          props.class
        )}
      >
        {slots.default?.()}
      </div>
    );
  },
});

const Title = defineComponent({
  name: "DialogTitle",
  props: {
    class: p.string(),
    as: p.string<keyof HTMLElementTagNameMap>("h2"),
    asChild: p.boolean(),
  },
  setup(props, { slots }) {
    return () => (
      <DialogTitle
        asChild={props.asChild}
        as={props.as}
        class={cn("text-lg font-semibold leading-none tracking-tight text-strong", props.class)}
      >
        {slots.default?.()}
      </DialogTitle>
    );
  },
});

const Description = defineComponent({
  name: "DialogDescription",
  props: {
    class: p.string(),
    as: p.string<keyof HTMLElementTagNameMap>("p"),
    asChild: p.boolean(),
  },
  setup(props, { slots }) {
    return () => (
      <DialogDescription
        {...props}
        class={cn("text-balance text-sm text-muted-foreground", props.class)}
      >
        {slots.default?.()}
      </DialogDescription>
    );
  },
});

export default {
  Root,
  Trigger,
  Content,
  Header,
  Body,
  Footer,
  Title,
  Description,
};
